import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [enrollmentsRes, classesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/enrollments/my-courses`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/classes/student/upcoming`)
      ]);
      setEnrollments(enrollmentsRes.data);
      setUpcomingClasses(classesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClass = async (classId) => {
    setSelectedClassId(classId);
    setShowPasswordModal(true);
    setError('');
    setPassword('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/classes/${selectedClassId}/join`,
        { password }
      );
      
      // Password verified, open proxy link (not direct Zoom link)
      window.open(response.data.proxyLink, '_blank');
      setShowPasswordModal(false);
      setPassword('');
      
      // Show success message
      if (response.data.accessCount > 1) {
        alert(`Welcome back! You've accessed this class ${response.data.accessCount} times.`);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-teal-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-extrabold mb-2">
            <span className="bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
              🎓 Student Dashboard
            </span>
          </h1>
          <p className="text-gray-700 font-medium text-lg">
            Welcome {user?.name}! Ready to learn today? 🚀
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-6 text-white shadow-xl transform hover:scale-105 transition-transform">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 font-semibold">My Courses</p>
                <p className="text-4xl font-extrabold mt-2">{enrollments.length}</p>
              </div>
              <div className="text-6xl opacity-50">📚</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl p-6 text-white shadow-xl transform hover:scale-105 transition-transform">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 font-semibold">Upcoming Classes</p>
                <p className="text-4xl font-extrabold mt-2">{upcomingClasses.length}</p>
              </div>
              <div className="text-6xl opacity-50">📅</div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl p-6 text-white shadow-xl transform hover:scale-105 transition-transform">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 font-semibold">Progress</p>
                <p className="text-4xl font-extrabold mt-2">75%</p>
              </div>
              <div className="text-6xl opacity-50">🎯</div>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-gradient-to-r from-green-50 to-teal-50 border-l-4 border-green-500 p-6 mb-8 rounded-xl shadow-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-extrabold text-green-900 mb-2">🔒 Your classes are secure</h3>
              <p className="text-sm text-green-800 font-medium">
                You'll need to verify your password each time you join a class. 
                Please do not share your meeting links with others. Your security is our priority!
              </p>
            </div>
          </div>
        </div>

        {/* Enrolled Courses */}
        <div className="mb-10">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">📚 My Courses</h2>
          {enrollments.length === 0 ? (
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center border-4 border-blue-200">
              <div className="text-6xl mb-4">📖</div>
              <p className="text-gray-600 text-lg font-medium">You are not enrolled in any courses yet.</p>
              <p className="text-gray-500 mt-2">Contact admin to enroll in courses!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {enrollments.map(enrollment => (
                <div key={enrollment._id} className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-2xl shadow-lg border-4 border-blue-200 hover:shadow-xl transition-all transform hover:-translate-y-1">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-2xl font-extrabold text-gray-900">{enrollment.course.title}</h3>
                    <span className="text-4xl">📖</span>
                  </div>
                  <p className="text-gray-700 mb-4 font-medium">{enrollment.course.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t-2 border-blue-200">
                    <span className="text-sm font-bold text-blue-600">
                      📂 {enrollment.course.category}
                    </span>
                    {enrollment.course.teacher && (
                      <span className="text-sm font-bold text-gray-700">
                        👨‍🏫 {enrollment.course.teacher.name}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Classes */}
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">⏰ Upcoming Classes</h2>
          {upcomingClasses.length === 0 ? (
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center border-4 border-blue-200">
              <div className="text-6xl mb-4">📅</div>
              <p className="text-gray-600 text-lg font-medium">No upcoming classes scheduled.</p>
              <p className="text-gray-500 mt-2">Check back later for new classes!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {upcomingClasses.map(classItem => (
                <div key={classItem._id} className="bg-white p-6 rounded-2xl shadow-xl border-l-4 border-green-500 hover:shadow-2xl transition-all">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-2xl font-extrabold text-gray-900">{classItem.title}</h3>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold animate-pulse">
                          LIVE SOON
                        </span>
                      </div>
                      <div className="space-y-2">
                        <p className="text-gray-700 font-medium">
                          <span className="text-green-600 font-bold">📚 Course:</span> {classItem.course.title}
                        </p>
                        <p className="text-gray-700 font-medium">
                          <span className="text-green-600 font-bold">👨‍🏫 Teacher:</span> {classItem.teacher.name}
                        </p>
                        <p className="text-gray-700 font-medium">
                          <span className="text-green-600 font-bold">📅 Time:</span> {new Date(classItem.scheduledAt).toLocaleString('en-IN', { 
                            dateStyle: 'full', 
                            timeStyle: 'short' 
                          })}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleJoinClass(classItem._id)}
                      className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-8 py-4 rounded-xl font-bold hover:from-green-600 hover:to-teal-600 shadow-xl transform hover:-translate-y-1 transition-all"
                    >
                      🚀 Join Class
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Password Verification Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full border-4 border-green-200">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">🔐</div>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Verify Your Identity</h2>
                <p className="text-gray-600 font-medium">
                  For security, please enter your password to join the class.
                </p>
              </div>
              
              {error && (
                <div className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4 font-medium">
                  ⚠️ {error}
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-2 font-bold">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent font-medium"
                    placeholder="Enter your password"
                    required
                    autoFocus
                  />
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    disabled={verifying}
                    className="flex-1 bg-gradient-to-r from-green-500 to-teal-500 text-white py-3 rounded-xl font-bold hover:from-green-600 hover:to-teal-600 disabled:from-gray-400 disabled:to-gray-400 shadow-lg transform hover:-translate-y-1 transition-all"
                  >
                    {verifying ? '⏳ Verifying...' : '✅ Verify & Join Class'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPassword('');
                      setError('');
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-400 shadow-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>

              <div className="mt-6 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                <p className="text-sm text-green-800 font-medium">🔒 This ensures only you can access your classes.</p>
                <p className="text-sm text-green-800 font-medium">Your password is never stored or shared.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;
